import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadSignPostingOrganisations } from '../../../../store/directusService';
import {
    clearSignPostings,
    deleteSignPosting,
    loadSignPostings
} from '../../../../store/participantService';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import {
    SIGNPOSTING_ORGANISATIONS_ERROR,
    SIGNPOSTINGS_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import EditSignPosting from './EditSignPosting';
import SignPostingRow from './SignPostingRow';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'date', numeric: false, label: 'Date' },
    { id: 'organisation', numeric: true, label: 'Organisation' },
    { id: 'type', numeric: true, label: 'Signposting Type' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'date',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const SignPosting = ({ participant, isDisabled = true, isDeleteDisabled = true }) => {
    const dispatch = useDispatch();
    const signPostingRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [loadingContentMessage, setLoadingContentMessage] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [openId, setOpenId] = useState('');

    // STORE STATE
    const signPostingOrganisations = useSelector(
        (state) => state.entities.directusService.signPostingOrganisations
    );
    const signPostings = useSelector((state) => state.entities.participantService.signPostings);
    const signPostingsMetaData = useSelector(
        (state) => state.entities.participantService.signPostingsMetaData
    );

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadSignPostingOrganisations());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        participant?.id && dispatch(loadSignPostings(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (signPostingOrganisations?.length < 1) return;
        if (signPostingsMetaData?.loaded) {
            if (!signPostings?.length) {
                setRows([]);
                setLoadingContentMessage(SIGNPOSTINGS_ERROR);
                return;
            }
            const rows = signPostings.map((el) => {
                const org = signPostingOrganisations.find(
                    (entry) => entry.id === el.organisationId
                );
                return {
                    ...el,
                    organisation: org?.name || '',
                    type:
                        org.signposting_type.find(
                            (entry) => entry.signposting_type_id.id === el.typeId
                        )?.signposting_type_id.name || ''
                };
            });
            setRows(rows);
        }
    }, [signPostings, signPostingOrganisations, signPostingsMetaData]);

    // FNS
    const loadingError = () => {
        if (signPostingOrganisations?.length < 1) return SIGNPOSTING_ORGANISATIONS_ERROR;
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <SignPostingRow
                key={el.id}
                row={el}
                isDisabled={isDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
                toggleDropdown={setOpenId}
                openId={openId}
            />
        ));

    const cleanUp = () => {
        dispatch(clearSignPostings());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setFormType('edit');
        setSelectedRow(row);
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteSignPosting(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(signPostingRef);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg)
        return (
            <div className={`${local.wellBeingSection} ${app.fadein}`}>
                <LoadingSpinner content={errorMsg} />
            </div>
        );

    // RENDER
    return (
        <div
            className={`${local.wellBeingSection} ${app.fadein}`}
            data-testid="signposting"
            ref={signPostingRef}>
            <h2 className={app.sectionHeading}>Signposting</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        defaultRowsPerPage={rowMetaData.rowsPerPage}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addSignPostingButton"
                        type="button"
                        content="Add New Signposting"
                        disabled={isDisabled}
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <EditSignPosting
                        participant={participant}
                        onClose={onClose}
                        isDisabled={isDisabled}
                        formType={formType}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

SignPosting.propTypes = {
    participant: PropTypes.object.isRequired,
    isDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool
};

export default SignPosting;
